import { TeamCollection, TeamMember } from './types'

const EXTRA_ENGAGED_NAMES = [
  'Chris Morgan',
  'Pat Rivera',
  'Jamie Ortiz',
  'Robin Hale',
  'Skyler Vance',
  'Dana Brooks',
  'Parker Quinn',
  'Reese Ames',
  'Taylor Knox',
  'Morgan Blake',
  'Casey Holt',
  'Avery Finch',
  'Jordan Shaw',
]

/** First 3 names are shown as avatars; remaining names appear in the +N tooltip. */
export const buildEngagedWith = (
  visibleNames: string[],
  totalCount: number
) => {
  const names = [...visibleNames]
  let extraIndex = 0
  while (names.length < totalCount) {
    const baseName =
      EXTRA_ENGAGED_NAMES[extraIndex % EXTRA_ENGAGED_NAMES.length]
    const cycle = Math.floor(extraIndex / EXTRA_ENGAGED_NAMES.length)
    names.push(cycle === 0 ? baseName : `${baseName} ${cycle + 1}`)
    extraIndex += 1
  }
  return names
}

const avatarFor = (id: string) => `https://i.pravatar.cc/150?u=${id}`

export type TeamRelationshipDummyResponse = {
  members: TeamMember[]
  collections: TeamCollection[]
  suggestedMemberIds: string[]
}

/**
 * Single temporary API-shaped payload until Team Relationship endpoints are wired.
 * One response populates members, collections (active + archived), and suggested picks.
 */
export const DUMMY_TEAM_RELATIONSHIP_RESPONSE: TeamRelationshipDummyResponse = {
  members: [
    {
      id: 'ae-floyd-ironwel',
      name: 'Floyd Ironwel',
      role: 'GenAI Growth Manager',
      location: 'Los Angeles',
      avatarUrl: avatarFor('ae-floyd-ironwel'),
      engagedWith: buildEngagedWith(
        ['Alex Kim', 'Jordan Lee', 'Sam Patel'],
        14
      ),
    },
    {
      id: 'ae-darrell-cooper',
      name: 'Darrell Cooper',
      role: 'GenAI Growth Account Director',
      location: 'New Mexico City',
      avatarUrl: avatarFor('ae-darrell-cooper'),
      engagedWith: buildEngagedWith(
        ['Casey Ng', 'Riley Chen', 'Morgan Diaz'],
        14
      ),
    },
    {
      id: 'ae-jacob-jones',
      name: 'Jacob Jones',
      role: 'GenAI Growth Manager',
      location: 'Los Angeles',
      avatarUrl: avatarFor('ae-jacob-jones'),
      engagedWith: buildEngagedWith(
        ['Taylor Brooks', 'Jamie Fox', 'Quinn Reed'],
        14
      ),
    },
    {
      id: 'ae-leslie-alexander',
      name: 'Leslie Alexander',
      role: 'GenAI Growth Manager',
      location: 'Los Angeles',
      avatarUrl: avatarFor('ae-leslie-alexander'),
      engagedWith: buildEngagedWith(
        ['Avery Cole', 'Drew Hayes', 'Blake West'],
        14
      ),
    },
    {
      id: 'ae-esther-howard',
      name: 'Esther Howard',
      role: 'Enterprise AE',
      location: 'New York',
      avatarUrl: avatarFor('ae-esther-howard'),
      engagedWith: buildEngagedWith(
        ['Cameron Ortiz', 'Reese Park', 'Harper Diaz'],
        9
      ),
    },
    {
      id: 'ae-jane-cooper',
      name: 'Jane Cooper',
      role: 'Strategic AE',
      location: 'Seattle',
      avatarUrl: avatarFor('ae-jane-cooper'),
      engagedWith: buildEngagedWith(
        ['Noah Grant', 'Ellie Shaw', 'Owen Price'],
        11
      ),
    },
    {
      id: 'ae-robert-fox',
      name: 'Robert Fox',
      role: 'Cloud Specialist',
      location: 'Austin',
      avatarUrl: avatarFor('ae-robert-fox'),
      engagedWith: buildEngagedWith(
        ['Liam Scott', 'Mia Torres', 'Ethan Bell'],
        7
      ),
    },
    {
      id: 'ae-brooklyn-simmons',
      name: 'Brooklyn Simmons',
      role: 'Growth Manager',
      location: 'Chicago',
      avatarUrl: avatarFor('ae-brooklyn-simmons'),
      engagedWith: buildEngagedWith(
        ['Sofia Lane', 'Lucas Hart', 'Zoe Miles'],
        12
      ),
    },
    {
      id: 'ae-wade-warren',
      name: 'Wade Warren',
      role: 'Partner AE',
      location: 'Boston',
      avatarUrl: avatarFor('ae-wade-warren'),
      engagedWith: ['Nina Cole', 'Owen Blake'],
    },
    {
      id: 'ae-cameron-williamson',
      name: 'Cameron Williamson',
      role: 'GenAI Growth Manager',
      location: 'San Francisco',
      avatarUrl: avatarFor('ae-cameron-williamson'),
      engagedWith: buildEngagedWith(
        ['Leo Dunn', 'Aria Wells', 'Kai Brooks'],
        16
      ),
    },
    {
      id: 'ae-jenny-wilson',
      name: 'Jenny Wilson',
      role: 'Mid-Market AE',
      location: 'Denver',
      avatarUrl: avatarFor('ae-jenny-wilson'),
      engagedWith: ['Chris Morgan'],
    },
    {
      id: 'ae-albert-flores',
      name: 'Albert Flores',
      role: 'Enterprise AE',
      location: 'Miami',
      avatarUrl: avatarFor('ae-albert-flores'),
      engagedWith: buildEngagedWith(
        ['Harper Diaz', 'Reese Park', 'Cameron Ortiz'],
        5
      ),
    },
  ],
  collections: [
    {
      id: 'col-la-startup',
      name: 'Los Angeles Startup Team',
      type: 'strategic_team',
      memberIds: [
        'ae-floyd-ironwel',
        'ae-darrell-cooper',
        'ae-jacob-jones',
        'ae-leslie-alexander',
        'ae-esther-howard',
        'ae-jane-cooper',
        'ae-robert-fox',
        'ae-brooklyn-simmons',
      ],
      createdAt: '2026-06-13T12:00:00.000Z',
      createdBy: 'Darrell Cooper',
      metrics: {
        closedWon: { count: 40, amount: 200_000 },
        inProgress: { count: 40, amount: 200_000 },
        closedLost: { count: 40, amount: 200_000 },
      },
      isArchived: false,
    },
    {
      id: 'col-ny-startup',
      name: 'New York Startup Team',
      type: 'geography',
      memberIds: [
        'ae-floyd-ironwel',
        'ae-jacob-jones',
        'ae-leslie-alexander',
        'ae-robert-fox',
        'ae-cameron-williamson',
        'ae-esther-howard',
        'ae-jane-cooper',
        'ae-wade-warren',
      ],
      createdAt: '2026-06-13T12:00:00.000Z',
      createdBy: 'Darrell Cooper',
      metrics: {
        closedWon: { count: 45, amount: 0 },
        inProgress: { count: 0, amount: 563_000 },
        closedLost: { count: 0, amount: 0 },
      },
      isArchived: false,
    },
    {
      id: 'col-enterprise-segment',
      name: 'Enterprise Segment Pod',
      type: 'segment',
      memberIds: [
        'ae-darrell-cooper',
        'ae-esther-howard',
        'ae-jane-cooper',
        'ae-albert-flores',
      ],
      createdAt: '2026-04-18T15:45:00.000Z',
      createdBy: 'Leslie Alexander',
      metrics: {
        closedWon: { count: 12, amount: 1_250_000 },
        inProgress: { count: 8, amount: 890_000 },
        closedLost: { count: 3, amount: 210_000 },
      },
      isArchived: false,
    },
    {
      id: 'col-named-fortune500',
      name: 'Fortune 500 Named Accounts',
      type: 'named_accounts',
      memberIds: [
        'ae-esther-howard',
        'ae-jane-cooper',
        'ae-brooklyn-simmons',
        'ae-wade-warren',
        'ae-jenny-wilson',
        'ae-albert-flores',
      ],
      createdAt: '2026-03-21T11:00:00.000Z',
      createdBy: 'Jane Cooper',
      metrics: {
        closedWon: { count: 6, amount: 2_400_000 },
        inProgress: { count: 11, amount: 1_750_000 },
        closedLost: { count: 2, amount: 400_000 },
      },
      isArchived: false,
    },
    {
      id: 'col-midmarket-growth',
      name: 'Mid-Market Growth',
      type: 'segment',
      memberIds: ['ae-brooklyn-simmons', 'ae-jenny-wilson'],
      createdAt: '2026-02-10T08:15:00.000Z',
      createdBy: 'Brooklyn Simmons',
      metrics: {
        closedWon: { count: 22, amount: 180_000 },
        inProgress: { count: 14, amount: 120_000 },
        closedLost: { count: 7, amount: 45_000 },
      },
      isArchived: false,
    },
    {
      id: 'col-northeast-geo-archived',
      name: 'Northeast Territory (Archived)',
      type: 'geography',
      memberIds: ['ae-esther-howard', 'ae-wade-warren'],
      createdAt: '2025-11-04T14:20:00.000Z',
      createdBy: 'Wade Warren',
      metrics: {
        closedWon: { count: 15, amount: 320_000 },
        inProgress: { count: 0, amount: 0 },
        closedLost: { count: 11, amount: 140_000 },
      },
      isArchived: true,
    },
    {
      id: 'col-partner-pod-archived',
      name: 'Partner Pursuit Pod',
      type: 'strategic_team',
      memberIds: [
        'ae-darrell-cooper',
        'ae-wade-warren',
        'ae-cameron-williamson',
      ],
      createdAt: '2025-09-16T10:00:00.000Z',
      createdBy: 'Darrell Cooper',
      metrics: {
        closedWon: { count: 9, amount: 670_000 },
        inProgress: { count: 1, amount: 50_000 },
        closedLost: { count: 4, amount: 88_000 },
      },
      isArchived: true,
    },
    {
      id: 'col-legacy-named-archived',
      name: 'Legacy Named Accounts',
      type: 'named_accounts',
      memberIds: ['ae-floyd-ironwel', 'ae-robert-fox', 'ae-albert-flores'],
      createdAt: '2025-07-01T16:40:00.000Z',
      createdBy: 'Robert Fox',
      metrics: {
        closedWon: { count: 4, amount: 95_000 },
        inProgress: { count: 0, amount: 0 },
        closedLost: { count: 18, amount: 260_000 },
      },
      isArchived: true,
    },
  ],
  suggestedMemberIds: [
    'ae-floyd-ironwel',
    'ae-darrell-cooper',
    'ae-jacob-jones',
    'ae-leslie-alexander',
  ],
}

export const normalizeCollections = (collections: TeamCollection[]) => {
  const collectionsById: Record<string, TeamCollection> = {}
  const collectionIds: string[] = []
  for (const collection of collections) {
    collectionsById[collection.id] = collection
    collectionIds.push(collection.id)
  }
  return { collectionsById, collectionIds }
}

export const normalizeMembers = (members: TeamMember[]) => {
  const membersById: Record<string, TeamMember> = {}
  const memberIds: string[] = []
  for (const member of members) {
    membersById[member.id] = member
    memberIds.push(member.id)
  }
  return { membersById, memberIds }
}

/** Normalize the single dummy response into store-ready slices. */
export const normalizeTeamRelationshipResponse = (
  response: TeamRelationshipDummyResponse = DUMMY_TEAM_RELATIONSHIP_RESPONSE
) => ({
  ...normalizeMembers(response.members),
  ...normalizeCollections(response.collections),
  suggestedMemberIds: response.suggestedMemberIds,
})

/** Convenience aliases used by existing call sites / specs. */
export const DUMMY_TEAM_MEMBERS = DUMMY_TEAM_RELATIONSHIP_RESPONSE.members
export const DUMMY_TEAM_COLLECTIONS =
  DUMMY_TEAM_RELATIONSHIP_RESPONSE.collections
export const DUMMY_SUGGESTED_MEMBER_IDS =
  DUMMY_TEAM_RELATIONSHIP_RESPONSE.suggestedMemberIds
